import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Tooltip } from '@c2fo/liquidity';
import { InfoCircleIcon } from '@c2fo/liquidity/icons';
import invoices from '@/enterprise/assets/invoicesgrey.svg?url';
import EmptyRender from '@/enterprise/components/EmptyRender';
import NoDataState from '@/enterprise/components/NoDataState';
import {
  Table,
  TableBody,
  TableCell,
  TableDisplay,
  TableHead,
  TableHeader,
  TableRow,
} from '@/enterprise/components/Table';
import useSubstitutedInvoices from '@/enterprise/data/useSubstitutedInvoices';
import useLocaleFormat from '@/utils/useLocaleFormat';
import { SubstitutionInvoiceSource, SubstitutionInvoiceUrlState } from './types';
import { paginate } from './utils';

export const SubstitutionsTable = ({
  setHasNextPage,
  urlState,
  source,
}: {
  setHasNextPage: (hasNextPage: boolean) => void;
  urlState: SubstitutionInvoiceUrlState;
  source: SubstitutionInvoiceSource;
}) => {
  const { t } = useTranslation();
  const { asDateStringText, asCurrency } = useLocaleFormat();
  const { makerOrganizationUuid, takerMarkets } = source;

  const { data } = useSubstitutedInvoices({
    makerOrganizationUuid,
    takerMarkets,
    search: urlState.searchValue,
  });

  const { rows, hasNextPage } = paginate(data ?? [], urlState);

  useEffect(() => {
    setHasNextPage(hasNextPage);
  }, [hasNextPage, setHasNextPage]);

  return (
    <div className="overflow-auto">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>{t('core.invoiceId')}</TableHead>
            <TableHead className="text-right">{t('core.invoiceAmount')}</TableHead>
            <TableHead>
              <div className="flex items-center justify-end gap-1">
                {t('core.originalDueDate')}
                <Tooltip>
                  <Tooltip.Trigger aria-label={t('core.originalDueDate')}>
                    <InfoCircleIcon className="fill-blue-500" />
                  </Tooltip.Trigger>
                  <Tooltip.Portal>
                    <Tooltip.Content
                      arrow
                      className="max-w-60 whitespace-pre-wrap text-center font-normal normal-case"
                      collisionPadding={24}
                      side="bottom"
                      variant="dark"
                    >
                      {t('invoiceCentral.substitutionOriginalDueDateTooltip')}
                    </Tooltip.Content>
                  </Tooltip.Portal>
                </Tooltip>
              </div>
            </TableHead>
            <TableHead className="text-right">{t('invoiceCentral.paymentReference')}</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.length > 0 ? (
            rows.map((invoice) => (
              <TableRow key={invoice.uuid}>
                {/* Invoice Id */}
                <TableCell>
                  <div className="max-w-80 whitespace-pre-wrap break-words">{invoice.invoiceId}</div>
                </TableCell>
                {/* Invoice Amount */}
                <TableCell className="text-right">{asCurrency(invoice.amount, invoice.currency)}</TableCell>
                {/* Original Due Date */}
                <TableCell className="text-right">{asDateStringText({ date: invoice.originalDueDate })}</TableCell>
                {/* Payment Reference */}
                <TableCell className="text-right">
                  <EmptyRender condition={!!invoice.paymentReference}>{invoice.paymentReference}</EmptyRender>
                </TableCell>
              </TableRow>
            ))
          ) : (
            <TableDisplay colSpan={4}>
              <NoDataState
                className="py-8"
                icon={<img src={invoices} alt="emptystatements" className="h-28 w-28" />}
                title={t('invoiceCentral.noSubstitutedInvoices')}
                message={t('invoiceCentral.noSubstitutedInvoicesDescription')}
              />
            </TableDisplay>
          )}
        </TableBody>
      </Table>
    </div>
  );
};

export default SubstitutionsTable;
